import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Alert, Collapse, Link } from '@mui/material';
import { Section, AiTag, Pill, Caption, T } from './shared';
import { updateNote, addTeamNote } from '../../../services/sprintHealthService';

const NumberBadge = ({ n, ghost }) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{
      display: 'inline-grid', placeItems: 'center', minWidth: 22, height: 22, borderRadius: '50%', flex: 'none',
      fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
      ...(ghost
        ? { border: `1.5px dashed ${T.line}`, color: T.muted, mt: 0.75 }
        : { bgcolor: T.tint, color: T.green, mr: 1.25, verticalAlign: '1px' }),
    }}
  >
    {n}
  </Box>
);

const TeamTag = () => (
  <Box component="span" sx={{ fontSize: '0.72rem', fontWeight: 600, borderRadius: '8px', px: 1, py: 0.25, bgcolor: T.tint, color: T.green, whiteSpace: 'nowrap' }}>
    Team note
  </Box>
);

const Evidence = ({ items }) => items.map((e, k) => (
  <Box
    key={k}
    component="span"
    title={e.source === 'tool' ? 'Found by the AI with a read-only tool' : 'From the sprint facts'}
    sx={{
      fontSize: '0.75rem', borderRadius: '6px', px: 1, py: 0.25, fontVariantNumeric: 'tabular-nums',
      ...(e.source === 'tool' ? { border: `1px dashed ${T.line}`, color: T.muted } : { bgcolor: T.tint }),
    }}
  >
    {e.text}
  </Box>
));

/**
 * Numbered sprint notes: AI-drafted (number-checked) and team-added. While editable, the team can
 * edit, hide, restore and add notes. Read-only for the cross-team (leadership) notes.
 */
const AiNotesCard = ({ notes, title, desc, editable, sprint, team, onChange, emptyHint }) => {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDropped, setShowDropped] = useState(false);

  const act = async (idx, body) => {
    setError(null);
    try {
      const res = await updateNote(sprint, team, idx, body);
      onChange?.(res.data);
      setEditing(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const text = newNote.trim();
    if (!text) { setError('Write the note first.'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await addTeamNote(sprint, team, text);
      onChange?.(res.data);
      setNewNote('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const list = notes?.notes || [];
  const hiddenCount = list.filter((n) => n.hidden).length;
  const teamCount = list.filter((n) => n.source === 'team').length;
  const hasAi = list.some((n) => n.source !== 'team');

  return (
    <Section
      title={title}
      desc={desc}
      right={editable ? <Box sx={{ ml: 'auto' }}><Pill tone="warn">Team review open</Pill></Box> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {notes?.error && !hasAi && <Alert severity="warning" sx={{ mb: 1.5 }}>The last attempt to draft AI notes failed: {notes.error}</Alert>}
      {!list.length && !notes?.error && <Typography variant="body2" color="text.secondary">{emptyHint || 'No notes for this sprint yet.'}</Typography>}

      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {list.map((n, pos) => (
          <Box
            component="li"
            key={n.idx}
            sx={{
              py: 1.75, borderBottom: `1px solid ${T.line2}`, '&:last-of-type': { borderBottom: 0 },
              display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,1fr) auto' }, gap: 0.75, columnGap: 2,
            }}
          >
            {editing === n.idx ? (
              <TextField id={`note-edit-${n.idx}`} multiline minRows={2} fullWidth value={draft} onChange={(e) => setDraft(e.target.value)} inputProps={{ 'aria-label': `Edit note ${pos + 1}` }} />
            ) : (
              <Typography sx={{ fontSize: '0.92rem', lineHeight: 1.5, ...(n.hidden && { color: T.muted, textDecoration: 'line-through' }) }}>
                <NumberBadge n={pos + 1} />
                {n.text}
                {n.edited && <Caption sx={{ display: 'inline', ml: 1 }}>edited by team</Caption>}
                {n.hidden && <Caption sx={{ display: 'inline', ml: 1 }}>hidden from email</Caption>}
              </Typography>
            )}
            {editable && (
              <Box sx={{ gridRow: { sm: '1 / span 3' }, gridColumn: { sm: 2 }, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                {editing === n.idx ? (
                  <>
                    <Button size="small" variant="contained" onClick={() => act(n.idx, { text: draft })}>Save</Button>
                    <Button size="small" onClick={() => setEditing(null)}>Cancel</Button>
                  </>
                ) : n.hidden ? (
                  <Link component="button" onClick={() => act(n.idx, { hidden: false })}>Restore</Link>
                ) : (
                  <>
                    <Link component="button" onClick={() => { setEditing(n.idx); setDraft(n.text); }}>Edit</Link>
                    <Link component="button" onClick={() => act(n.idx, { hidden: true })}>Hide</Link>
                  </>
                )}
              </Box>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {n.source === 'team' ? <TeamTag /> : <AiTag />}
              <Evidence items={n.evidence || []} />
            </Box>
            {n.question && (
              <Typography variant="body2" sx={{ color: T.muted }}>
                <Box component="b" sx={{ color: T.ink, fontWeight: 600 }}>Worth asking:</Box> {n.question}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {editable && (
        <Box component="form" onSubmit={add} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mt: list.length ? 1.5 : 1, pt: list.length ? 1.5 : 0, borderTop: list.length ? `1px solid ${T.line2}` : 0 }}>
          <NumberBadge n={list.length + 1} ghost />
          <TextField
            id="note-new"
            multiline
            minRows={2}
            fullWidth
            size="small"
            placeholder="Add a note for this sprint, e.g. a win, a risk or context leadership should know"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            inputProps={{ 'aria-label': 'New note' }}
          />
          <Button type="submit" variant="contained" size="small" disabled={saving} sx={{ mt: 0.5, whiteSpace: 'nowrap' }}>{saving ? 'Adding…' : 'Add note'}</Button>
        </Box>
      )}

      {notes?.dropped?.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Link component="button" variant="body2" onClick={() => setShowDropped((v) => !v)}>
            {notes.dropped.length} AI draft note{notes.dropped.length > 1 ? 's were' : ' was'} dropped by the checker
          </Link>
          <Collapse in={showDropped}>
            {notes.dropped.map((d, k) => <Caption key={k} sx={{ mt: 0.75 }}>{d.reason} AI notes are only kept when every number and ID they cite is in the facts. Team notes are not checked.</Caption>)}
          </Collapse>
        </Box>
      )}
      {list.length > 0 && (
        <Caption sx={{ mt: 1.5 }}>
          {hasAi ? 'AI notes are drafted by Claude from this sprint\'s facts; dashed chips were found with read-only tools (ADO, GitHub).' : ''}
          {teamCount ? ` ${teamCount} team note${teamCount > 1 ? 's' : ''} added.` : ''}
          {hiddenCount ? ` ${hiddenCount} hidden note${hiddenCount > 1 ? 's' : ''} won't appear in the email.` : ''}
        </Caption>
      )}
    </Section>
  );
};

export default AiNotesCard;
